import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useCustomQueryParams } from 'src/components/CustomQueryParam/CustomQueryParam';
import { PageLoader } from 'src/components/PageLoader';
import { apiClient, onApiRequest } from 'src/modules/api';
import type { ApiRequest, PreflightSuccess } from 'src/types/api';

import { redirectWithError } from '../../pages/ConsentPage/redirect';

import { PreflightError } from '.';

type PreflightRequest = ApiRequest & Required<Pick<ApiRequest, 'client_id'>>;

export interface PreflightProps {
  preflight: PreflightSuccess;
  request: PreflightRequest;
  state?: string | null;
}

export type ErrorMessage =
  | 'Please navigate back to the apply form.'
  | 'Please try again later.';

const messages: Record<string, ErrorMessage> = {
  clientError: 'Please navigate back to the apply form.',
  serverError: 'Please try again later.',
};

const PreflightContext = createContext<PreflightProps | undefined>(undefined);

export const PreflightTestProvider = ({
  children,
}: {
  children: ReactNode;
}) => (
  <PreflightContext.Provider
    value={{
      preflight: {
        hirerName: 'Test hirer',
        partnerName: 'Test partner',
        redirectUrl: 'test-redirect-url',
        scope: 'r_profile_apply',
        sessionId: 'test-session-id',
        shouldShowProfileEdit: true,
        inShareableProfileExperiment: true,
        shouldShowShareableProfile: true,
      },
      request: {
        client_id: 'test-client-id',
      },
      state: undefined,
    }}
  >
    {children}
  </PreflightContext.Provider>
);

export const PreflightProvider = ({ children }: { children: ReactNode }) => {
  const {
    showProfileEdit: overrideShowProfileEdit,
    showShareableExperiment: overrideShowShareableExperiment,
    ...restOfParams
  } = useCustomQueryParams();

  const [preflight, setPreflight] = useState<PreflightSuccess>();
  const [apiError, setApiError] = useState<ErrorMessage>();

  const onError = useCallback(() => {
    setApiError(messages.serverError);
  }, []);

  const request = useMemo(
    () => ({
      advertiser_id: restOfParams.advertiser_id
        ? restOfParams.advertiser_id
        : undefined,
      client_id: restOfParams.client_id ?? undefined,
      redirect_uri: restOfParams.redirect_uri ?? undefined,
      response_type: restOfParams.response_type ?? undefined,
      scope: restOfParams.scope ?? undefined,
    }),
    [
      restOfParams.advertiser_id,
      restOfParams.client_id,
      restOfParams.redirect_uri,
      restOfParams.response_type,
      restOfParams.scope,
    ],
  );

  const onLoad = useCallback(async () => {
    // We need a `client_id` at minimum to validate the redirect URL.
    if (!request.client_id) {
      return;
    }

    const onSuccess = async () => {
      const response = await apiClient.preflight(request);

      if (response.ok) {
        apiClient.publish({
          ...request,
          sessionId: response.sessionId,
          action: 'preflight',
        });

        return setPreflight(response);
      }

      const { redirectUrl } = response;

      if (redirectUrl) {
        redirectWithError(
          { redirectUrl, state: restOfParams.state },
          response.error,
        );
      } else {
        setApiError(messages.clientError);
      }
    };

    await onApiRequest(onSuccess, onError);
  }, [onError, request, restOfParams.state]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  if (typeof apiError === 'string' || !request.client_id) {
    return <PreflightError error={apiError ?? messages.clientError} />;
  }

  if (!preflight) {
    return <PageLoader />;
  }

  return (
    <PreflightContext.Provider
      value={{
        preflight: {
          ...preflight,
          shouldShowProfileEdit:
            Boolean(overrideShowProfileEdit) || preflight.shouldShowProfileEdit,
          shouldShowShareableProfile:
            preflight.inShareableProfileExperiment ||
            Boolean(overrideShowShareableExperiment),
        },
        request: {
          ...request,
          client_id: request.client_id,
        },
        state: restOfParams.state,
      }}
    >
      {children}
    </PreflightContext.Provider>
  );
};

export const usePreflight = () => {
  const preflight = useContext(PreflightContext);

  if (!preflight) {
    throw new Error('usePreflight must be wrapped in a PreflightProvider.');
  }

  return preflight;
};
